import posthog from 'posthog-js';
import { ghostToken } from '@repo/stores/Ghost';

const EMAIL_KEY_PATTERN = /email/i;
const EMAIL_VALUE_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Reactive declaration to track impersonation state
let isImpersonating = ghostToken !== null;

ghostToken.subscribe((token) => {
    isImpersonating = token !== null;
});

function sanitizePostHogProperties(value) {
    if (value === null || value === undefined) {
        return value;
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => sanitizePostHogProperties(item))
            .filter((item) => item !== undefined);
    }

    if (typeof value !== 'object') {
        if (typeof value === 'string' && EMAIL_VALUE_PATTERN.test(value)) {
            return undefined;
        }

        return value;
    }

    return Object.entries(value).reduce((acc, [key, val]) => {
        if (EMAIL_KEY_PATTERN.test(key)) {
            return acc;
        }

        const sanitizedVal = sanitizePostHogProperties(val);

        if (sanitizedVal !== undefined) {
            acc[key] = sanitizedVal;
        }

        return acc;
    }, {});
}

/**
 * Helper function to call posthog.capture with required options.
 * @param {string} eventName - The name of the event to capture.
 * @param {object} options - Additional options for the event.
 */
export function postHogCapture(eventName, options = {}) {
    if (!eventName) {
        console.error('Event name is required for posthog.capture()');
        return;
    }

    // Check if PostHog is loaded before attempting to capture
    if (!posthog.__loaded) {
        console.warn('PostHog is not yet loaded, skipping event:', eventName);
        return;
    }

    function maskPasswords(obj) {
        delete obj.target;
        delete obj._target;
        for (const key in obj) {
            if (
                key.toLowerCase().includes('password') ||
                key.toLowerCase().includes('confirm-password') ||
                key.toLowerCase().includes('confirmpassword')
            ) {
                obj[key] = '****'; // Masking the password
            }

            if (typeof obj[key] === 'object' && obj[key] !== null) {
                maskPasswords(obj[key]); // Recursive call for nested objects
            }
        }
        return obj;
    }

    try {
        const maskedData = maskPasswords(options);

        const sanitizedData = sanitizePostHogProperties(maskedData);

        const eventOptions = {
            ...(sanitizedData && typeof sanitizedData === 'object' && !Array.isArray(sanitizedData) ? sanitizedData : {}),
            isImpersonating,
        };

        posthog.capture(eventName, eventOptions);
    } catch (error) {
        console.error('Error capturing event', error);
    }
}

/**
 * Identify a user with PostHog
 * @param {string} userId - The user ID
 * @param {object} properties - User properties
 */
export function postHogIdentify(userId, properties = {}) {
    if (!userId) {
        console.error('User ID is required for posthog.identify()');
        return;
    }

    if (!posthog.__loaded) {
        console.warn('PostHog is not yet loaded, skipping identify for user:', userId);
        return;
    }

    try {
        const sanitizedProperties = sanitizePostHogProperties(properties);

        posthog.identify(userId, {
            ...(sanitizedProperties && typeof sanitizedProperties === 'object' && !Array.isArray(sanitizedProperties) ? sanitizedProperties : {}),
            isImpersonating,
        });
    } catch (error) {
        console.error('Error identifying user', error);
    }
}

/**
 * Reset PostHog user identity (useful for logout)
 */
export function postHogReset() {
    if (!posthog.__loaded) {
        console.warn('PostHog is not yet loaded, skipping reset');
        return;
    }

    try {
        posthog.reset();
    } catch (error) {
        console.error('Error resetting PostHog', error);
    }
}

export function postHogPeopleSet(properties = {}) {
    if (!posthog.__loaded) {
        console.warn('PostHog is not yet loaded, skipping people.set');
        return;
    }

    try {
        const sanitizedProperties = sanitizePostHogProperties(properties);

        if (
            !sanitizedProperties ||
            typeof sanitizedProperties !== 'object' ||
            Array.isArray(sanitizedProperties) ||
            Object.keys(sanitizedProperties).length === 0
        ) {
            return;
        }

        posthog.people?.set?.(sanitizedProperties);
    } catch (error) {
        console.error('Error updating PostHog person properties', error);
    }
}

export { posthog };
